import React, { useCallback, useState } from "react";
import { useTheme } from "@emotion/react";
import {
  alpha,
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Link,
  useMediaQuery,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton"; // Correct import
import Logo from "src/components/logo";
// Remove this import: import { bgGradient } from "src/theme/css";
import AuroraBackground from "src/components/AuroraBackground"; // Add this import
import { useNavigate, useParams } from "react-router-dom";
import Iconify from "src/components/iconify";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import AuthService from "src/services/Auth.service";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";

const ChangePasswordView = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { email } = useParams();

  const [showPassword, setShowPassword] = useState(false);
  const [showconfirmPassword, setShowconfirmPassword] = useState(false);

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
    otp: Yup.string()
      .required("OTP is required")
      .min(6, "OTP must be 6 digits"),
  });

  const formik = useFormik({
    initialValues: { password: "", confirmPassword: "", otp: "" },
    validationSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      onSubmit(values, setSubmitting, resetForm);
    },
  });

  const onSubmit = useCallback(
    async (formData, setSubmitting, reset) => {
      try {
        setSubmitting(true);
        const res = await AuthService.resetPasswordWithOTP({
          email: email,
          newPassword: formData.password,
          confirmPassword: formData.confirmPassword,
          otp: formData.otp,
        });
        if (res.success) {
          toast.success(res.message);
          navigate("/login");
        } else {
          toast.error(res.message);
        }
        reset();
      } catch (error) {
        toast.error("Error in resetting password:", error);
      } finally {
        setSubmitting(false);
      }
    },
    [email, navigate]
  );

  const renderForm = (
    <form onSubmit={formik.handleSubmit}>
      <Stack spacing={3}>
        <TextField
          name="otp"
          label="Enter OTP"
          variant="outlined"
          type="number"
          value={formik.values.otp}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          error={formik.touched.otp && Boolean(formik.errors.otp)}
          helperText={formik.touched.otp && formik.errors.otp}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(15px)",
              backgroundColor: alpha("#60a5fa", 0.08), // Aurora blue background
              "& fieldset": {
                borderColor: alpha("#60a5fa", 0.3), // Aurora blue border
              },
              "&:hover fieldset": {
                borderColor: alpha("#3b82f6", 0.6), // Stronger blue on hover
                boxShadow: `0 0 10px ${alpha("#60a5fa", 0.2)}`, // Blue glow effect
              },
              "&.Mui-focused fieldset": {
                borderColor: "#60a5fa", // Full aurora blue when focused
                boxShadow: `0 0 15px ${alpha("#60a5fa", 0.3)}`, // Enhanced glow
              },
            },
          }}
          InputProps={{
            maxLength: 6,
            minLength: 6,
            endAdornment: (
              <InputAdornment position="end">
                <VpnKeyRoundedIcon
                  fontSize="medium"
                  sx={{
                    color: "#60a5fa", // Aurora blue color
                    filter: `drop-shadow(0 0 4px ${alpha("#60a5fa", 0.4)})`, // Blue glow
                  }}
                />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          type={showPassword ? "text" : "password"}
          name="password"
          label="New Password"
          value={formik.values.password}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(15px)",
              backgroundColor: alpha("#60a5fa", 0.08), // Aurora blue background
              "& fieldset": {
                borderColor: alpha("#60a5fa", 0.3), // Aurora blue border
              },
              "&:hover fieldset": {
                borderColor: alpha("#3b82f6", 0.6), // Stronger blue on hover
                boxShadow: `0 0 10px ${alpha("#60a5fa", 0.2)}`, // Blue glow effect
              },
              "&.Mui-focused fieldset": {
                borderColor: "#60a5fa", // Full aurora blue when focused
                boxShadow: `0 0 15px ${alpha("#60a5fa", 0.3)}`, // Enhanced glow
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowPassword((prev) => !prev)}
                  sx={{
                    color: "#60a5fa", // Aurora blue color
                    "&:hover": {
                      backgroundColor: alpha("#60a5fa", 0.1), // Blue hover background
                      color: "#3b82f6", // Darker blue on hover
                    },
                  }}
                >
                  <Iconify
                    icon={showPassword ? "eva:eye-fill" : "eva:eye-off-fill"}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          type={showconfirmPassword ? "text" : "password"}
          label="Confirm New Password"
          name="confirmPassword"
          value={formik.values.confirmPassword}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={
            formik.touched.confirmPassword &&
            Boolean(formik.errors.confirmPassword)
          }
          helperText={
            formik.touched.confirmPassword && formik.errors.confirmPassword
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              backdropFilter: "blur(15px)",
              backgroundColor: alpha("#60a5fa", 0.08), // Aurora blue background
              "& fieldset": {
                borderColor: alpha("#60a5fa", 0.3), // Aurora blue border
              },
              "&:hover fieldset": {
                borderColor: alpha("#3b82f6", 0.6), // Stronger blue on hover
                boxShadow: `0 0 10px ${alpha("#60a5fa", 0.2)}`, // Blue glow effect
              },
              "&.Mui-focused fieldset": {
                borderColor: "#60a5fa", // Full aurora blue when focused
                boxShadow: `0 0 15px ${alpha("#60a5fa", 0.3)}`, // Enhanced glow
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  edge="end"
                  onClick={() => setShowconfirmPassword((prev) => !prev)}
                  sx={{
                    color: "#60a5fa", // Aurora blue color
                    "&:hover": {
                      backgroundColor: alpha("#60a5fa", 0.1), // Blue hover background
                      color: "#3b82f6", // Darker blue on hover
                    },
                  }}
                >
                  <Iconify
                    icon={
                      showconfirmPassword ? "eva:eye-fill" : "eva:eye-off-fill"
                    }
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        color="primary"
        disabled={formik.isSubmitting}
        sx={{
          my: 3,
          boxShadow: `0 4px 15px ${alpha("#60a5fa", 0.3)}`, // Aurora blue shadow
          "&:hover": {
            boxShadow: `0 6px 20px ${alpha("#60a5fa", 0.4)}`, // Enhanced glow on hover
            transform: "translateY(-1px)",
          },
        }}
      >
        {formik.isSubmitting ? (
          <CircularProgress size={24} />
        ) : (
          "Reset Password"
        )}
      </LoadingButton>

      <Typography variant="body1" sx={{ textAlign: "center" }}>
        Remember your password?{" "}
        <Link
          color="inherit"
          variant="subtitle2"
          sx={{ ml: 0.5, cursor: "pointer" }}
          onClick={() => navigate("/login")}
        >
          Sign in
        </Link>
      </Typography>
    </form>
  );

  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));

  return (
    // Aurora Background with space theme
    <AuroraBackground
      showRadialGradient={true}
      minSpeed={10}
      maxSpeed={50}
      minDelay={800}
      maxDelay={2000}
      starColor="#60a5fa"
      trailColor="#93c5fd"
      starWidth={10}
      starHeight={4}
    >
      <Logo
        sx={{
          position: "fixed",
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          zIndex: 10, // Ensure logo stays on top
        }}
      />

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-evenly"
        sx={{
          height: "100%",
          width: "100%",
          position: "relative",
          zIndex: 1, // Ensure content stays above background
        }}
      >
        <Card
          sx={{
            p: 5,
            width: 1,
            maxWidth: 500,
            // Glassmorphism effect
            backdropFilter: "blur(15px)",
            backgroundColor:
              isSmUp &&
              (theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.1)
                : alpha(theme.palette.background.paper, 0.2)),
            border:
              isSmUp && `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
            borderRadius: isSmUp ? 2 : 0,
            position: "relative",
            overflow: "hidden",
            // Enhanced shadow with multiple layers
            boxShadow:
              isSmUp &&
              `
                       0 8px 32px ${alpha(theme.palette.common.black, 0.1)},
                       0 4px 16px ${alpha(theme.palette.common.black, 0.05)},
                       inset 0 2px 2px ${alpha(
                         theme.palette.common.white,
                         0.15
                       )}
                     `,
            // Subtle gradient overlay
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              background: `linear-gradient(90deg, 
                         transparent 0%, 
                         ${alpha(theme.palette.primary.light, 0.4)} 70%, 
                         transparent 100%
                       )`,
            },
            // Additional frosted glass effect
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                isSmUp &&
                (theme.palette.mode === "dark"
                  ? `linear-gradient(135deg,
                             ${alpha(theme.palette.primary.main, 0.02)} 0%,
                             ${alpha(theme.palette.background.paper, 0.01)} 100%
                           )`
                  : `linear-gradient(135deg,
                             ${alpha(theme.palette.primary.main, 0.03)} 0%,
                             ${alpha(theme.palette.background.paper, 0.04)} 100%
                           )`),
              pointerEvents: "none",
              zIndex: -1,
            },
          }}
        >
          <Typography variant="h3" textAlign="center">
            Reset Password
          </Typography>

          <Typography
            variant="body1"
            sx={{ mt: 2, mb: 5, textAlign: "center" }}
          >
            Enter your OTP and new password to reset your account
          </Typography>

          {renderForm}
        </Card>
      </Stack>
    </AuroraBackground>
  );
};

export default ChangePasswordView;
